import { useCallback, useEffect, useRef, useState } from 'react'

interface DiscoPlayerState {
  playerId: string
  player: HTMLAudioElement | null
  duration: number
  currentTime: number
  isPlaying: boolean
  isReady: boolean
  error: string | null
  togglePlayPause: () => void
  seekTo: (_time: number) => void
  pause: () => void
}

type DiscoLinkInfo = {
  audio_url?: string
  duration: number
  id: string
  thumbnail?: string
  title?: string
  provider?: string
  type?: string
} | null

export const useDiscoPlayer = (
  discoLinkInfo: DiscoLinkInfo | null,
): DiscoPlayerState => {
  const [player, setPlayer] = useState<HTMLAudioElement | null>(null)
  const [duration, setDuration] = useState(discoLinkInfo?.duration || 0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [processedUrl, setProcessedUrl] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const playerIdRef = useRef(`disco-player-${Date.now()}`)
  const trackId = discoLinkInfo ? parseInt(discoLinkInfo.id, 10) : null

  const isInitializedRef = useRef(false)
  const currentTrackIdRef = useRef<string | null>(null)
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const cleanupFunctionRef = useRef<(() => void) | null>(null)
  const playStateTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)

  // Manual time update interval when playing (backup for timeupdate events)
  useEffect(() => {
    if (isPlaying && audioRef.current && isReady) {
      timeUpdateIntervalRef.current = setInterval(() => {
        if (audioRef.current && !audioRef.current.paused) {
          const newTime = audioRef.current.currentTime
          setCurrentTime(newTime)
        }
      }, 100) // Update every 100ms
    } else {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current)
        timeUpdateIntervalRef.current = null
      }
    }

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current)
        timeUpdateIntervalRef.current = null
      }
    }
  }, [isPlaying, isReady])

  useEffect(() => {
    if (discoLinkInfo?.audio_url) {
      try {
        const updatedUrl = new URL(discoLinkInfo.audio_url)
        setProcessedUrl(updatedUrl.toString())
        setError(null)
      } catch (err) {
        console.error(
          '[useDiscoPlayer] Invalid audio URL:',
          discoLinkInfo.audio_url,
          err,
        )
        setProcessedUrl(null)
        setError('Invalid audio URL')
        return
      }
    } else {
      setProcessedUrl(null)
      setError(null)
    }
  }, [discoLinkInfo?.audio_url])

  // Initialize audio player
  useEffect(() => {
    if (!processedUrl) return

    let isMounted = true

    const initPlayer = async () => {
      try {
        // Prevent double initialization for the same track
        if (
          isInitializedRef.current &&
          currentTrackIdRef.current === String(trackId)
        ) {
          // Reset position to 0 when reusing existing player
          if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.currentTime = 0
            setCurrentTime(0)
            setIsPlaying(false)
          }
          return
        }

        // If track changed, reset initialization
        if (!isMounted) return

        // Clean up previous audio instance if it exists
        if (cleanupFunctionRef.current) {
          cleanupFunctionRef.current()
          cleanupFunctionRef.current = null
        }

        // Create or update HTML5 audio element (Apple Music style)
        if (!audioRef.current) {
          audioRef.current = new Audio(processedUrl)
        } else {
          audioRef.current.src = processedUrl
        }

        const audio = audioRef.current
        currentTrackIdRef.current = String(trackId)

        // Set up event listeners
        const handleLoadedData = () => {
          if (!isMounted) return
          const audioDuration = audio.duration || discoLinkInfo?.duration || 0
          setDuration(audioDuration)
          setIsReady(true)
          setError(null)
          isInitializedRef.current = true
        }

        const handleTimeUpdate = () => {
          if (!isMounted) return
          setCurrentTime(audio.currentTime)
        }

        const handlePlay = () => {
          if (!isMounted) return
          setIsPlaying(true)
        }

        const handlePause = () => {
          if (!isMounted) return
          setIsPlaying(false)
        }

        const handleEnded = () => {
          if (!isMounted) return
          setIsPlaying(false)
          setCurrentTime(0)
        }

        const handleError = () => {
          if (!isMounted) return
          setError('Failed to load audio')
          setIsReady(false)
        }

        // Add event listeners
        audio.addEventListener('loadeddata', handleLoadedData)
        audio.addEventListener('timeupdate', handleTimeUpdate)
        audio.addEventListener('play', handlePlay)
        audio.addEventListener('pause', handlePause)
        audio.addEventListener('ended', handleEnded)
        audio.addEventListener('error', handleError)

        // Set player state
        setPlayer(audio)

        // Load the audio
        audio.load()

        // Store cleanup function in ref - capture audio reference in closure
        cleanupFunctionRef.current = () => {
          audio.removeEventListener('loadeddata', handleLoadedData)
          audio.removeEventListener('timeupdate', handleTimeUpdate)
          audio.removeEventListener('play', handlePlay)
          audio.removeEventListener('pause', handlePause)
          audio.removeEventListener('ended', handleEnded)
          audio.removeEventListener('error', handleError)
        }
      } catch (err) {
        if (!isMounted) return
        console.error('[useDiscoPlayer] Init error:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
        setIsReady(false)
      }
    }

    initPlayer()

    return () => {
      isMounted = false

      // Clean up audio element
      if (audioRef.current) {
        audioRef.current.pause()

        if (cleanupFunctionRef.current) {
          cleanupFunctionRef.current()
          cleanupFunctionRef.current = null
        }

        audioRef.current = null
        setPlayer(null)
      }

      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current)
        timeUpdateIntervalRef.current = null
      }

      isInitializedRef.current = false
      currentTrackIdRef.current = null
    }
  }, [processedUrl, trackId, discoLinkInfo?.duration])

  const togglePlayPause = useCallback(() => {
    if (playStateTimeoutRef.current) {
      clearTimeout(playStateTimeoutRef.current)
      playStateTimeoutRef.current = null
    }

    try {
      if (isPlaying) {
        audioRef.current.pause()

        // Force update React state if audio is actually paused
        playStateTimeoutRef.current = setTimeout(() => {
          if (audioRef.current && audioRef.current?.paused) {
            setIsPlaying(false)
          }
          playStateTimeoutRef.current = null
        }, 50)
      } else {
        const playPromise = audioRef.current.play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Force update React state if audio is actually playing
              if (
                isMountedRef.current &&
                audioRef.current &&
                !audioRef.current.paused
              ) {
                setIsPlaying(true)
              }
            })
            .catch(error => {
              if (isMountedRef.current) {
                console.error('[useDiscoPlayer] Playback failed:', error)
                setError('Unable to play this track')
              }
            })
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        console.error('[useDiscoPlayer] Toggle play/pause error:', err)
        setError('Unable to play this track')
      }
    }
  }, [isPlaying])

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false

      // Clear timeout on unmount
      if (playStateTimeoutRef.current) {
        clearTimeout(playStateTimeoutRef.current)
        playStateTimeoutRef.current = null
      }
    }
  }, [])

  const seekTo = useCallback(
    (time: number) => {
      if (!audioRef.current || !isReady) {
        console.warn('Cannot seek: audio not ready', {
          hasAudio: !!audioRef.current,
          isReady,
          time,
        })
        return
      }

      try {
        audioRef.current.currentTime = Math.max(0, Math.min(time, duration))
        // Play track on seek if not already playing on click of waveform
        if (!isPlaying) {
          audioRef.current.play()
          setCurrentTime(audioRef.current.currentTime)
          setIsPlaying(true)
        }
      } catch (err) {
        console.error('[useDiscoPlayer] Seek error:', err)
      }
    },
    [duration, isReady, isPlaying],
  )

  const pause = useCallback(() => {
    if (!audioRef.current) {
      console.warn('Cannot pause: audio not available')
      return
    }

    try {
      audioRef.current.pause()
    } catch (err) {
      console.error('[useDiscoPlayer] Pause error:', err)
    }
  }, [])

  return {
    playerId: playerIdRef.current,
    player,
    duration,
    currentTime,
    isPlaying,
    isReady,
    error,
    togglePlayPause,
    seekTo,
    pause,
  }
}
